import { useEffect, useState, type FormEvent } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { categories } from "@/mocks/categories";

export default function Navbar() {
  const navigate = useNavigate();
  const location = useLocation();
  const [keyword, setKeyword] = useState("");
  const [mobileOpen, setMobileOpen] = useState(false);
  const [catOpen, setCatOpen] = useState(false);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);

  useEffect(() => {
    setMobileOpen(false);
    setCatOpen(false);
    setMobileSearchOpen(false);
  }, [location.pathname, location.search]);

  const submitSearch = (e: FormEvent) => {
    e.preventDefault();
    const q = keyword.trim();
    if (!q) return;
    navigate(`/search?q=${encodeURIComponent(q)}`);
    setKeyword("");
    setMobileOpen(false);
    setMobileSearchOpen(false);
  };

  const navLinkClass = ({ isActive }: { isActive: boolean }) =>
    `px-4 py-2 rounded-md text-sm font-medium whitespace-nowrap transition-colors ${
      isActive ? "text-primary-600 bg-primary-100" : "text-foreground-700 hover:text-primary-600 hover:bg-background-100"
    }`;

  return (
    <header className="sticky top-0 z-50 bg-background-50/95 backdrop-blur border-b border-background-200">
      <div className="w-full px-4 md:px-6">
        <div className="h-16 flex items-center gap-4">
          <Link to="/" className="flex items-center gap-2 shrink-0">
            <span className="w-9 h-9 bg-primary-500 rounded-lg flex items-center justify-center">
              <i className="ri-fire-line text-background-50 text-xl" />
            </span>
            <span className="text-xl font-extrabold text-foreground-950 tracking-tight" style={{ fontFamily: "var(--font-heading)" }}>
              吃瓜<span className="text-primary-500">网站</span>
            </span>
          </Link>

          <nav className="hidden md:flex items-center gap-1 ml-2">
            <NavLink to="/" end className={navLinkClass}>
              首页
            </NavLink>
            <div className="relative" onMouseEnter={() => setCatOpen(true)} onMouseLeave={() => setCatOpen(false)}>
              <button
                type="button"
                onClick={() => setCatOpen((v) => !v)}
                className="px-4 py-2 rounded-md text-sm font-medium text-foreground-700 hover:text-primary-600 hover:bg-background-100 whitespace-nowrap flex items-center gap-1 cursor-pointer transition-colors"
              >
                分类
                <i className={`ri-arrow-down-s-line transition-transform ${catOpen ? "rotate-180" : ""}`} />
              </button>
              {catOpen && (
                <div className="absolute left-0 top-full pt-2 w-[520px]">
                  <div className="bg-background-50 border border-background-200 rounded-lg p-3 grid grid-cols-2 gap-1 shadow-sm">
                    {categories.map((c) => (
                      <Link
                        key={c.slug}
                        to={`/category/${c.slug}`}
                        className="flex items-center gap-2 px-3 py-2 rounded-md hover:bg-background-100 text-sm text-foreground-700 hover:text-primary-600 transition-colors"
                      >
                        <i className={`${c.icon} text-primary-500`} />
                        {c.name}
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <NavLink to="/search" className={navLinkClass}>
              搜索
            </NavLink>
            <NavLink to="/about" className={navLinkClass}>
              关于
            </NavLink>
            <NavLink to="/contact" className={navLinkClass}>
              联系
            </NavLink>
          </nav>

          <form onSubmit={submitSearch} className="hidden md:flex ml-auto items-center">
            <div className="relative">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm" />
              <input
                type="text"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜索明星、影视、八卦..."
                className="w-56 lg:w-72 h-10 pl-9 pr-3 rounded-full bg-background-100 border border-background-200 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-300"
              />
            </div>
          </form>

          <button
            type="button"
            onClick={() => setMobileSearchOpen((v) => !v)}
            className="md:hidden ml-auto w-10 h-10 flex items-center justify-center rounded-md text-foreground-700 hover:bg-background-100 cursor-pointer"
            aria-label="搜索"
          >
            <i className="ri-search-line text-xl" />
          </button>
          <button
            type="button"
            onClick={() => setMobileOpen((v) => !v)}
            className="md:hidden w-10 h-10 flex items-center justify-center rounded-md text-foreground-700 hover:bg-background-100 cursor-pointer"
            aria-label="菜单"
          >
            <i className={`${mobileOpen ? "ri-close-line" : "ri-menu-line"} text-2xl`} />
          </button>
        </div>

        {mobileSearchOpen && (
          <form onSubmit={submitSearch} className="md:hidden pb-3">
            <div className="relative">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm" />
              <input
                type="text"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜索明星、影视、八卦..."
                className="w-full h-10 pl-9 pr-3 rounded-full bg-background-100 border border-background-200 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400"
              />
            </div>
          </form>
        )}
      </div>

      {mobileOpen && (
        <div className="md:hidden border-t border-background-200 bg-background-50 px-4 py-3 max-h-[70vh] overflow-y-auto">
          <div className="flex flex-col gap-1 mb-3">
            <Link to="/" className="px-3 py-2.5 rounded-md text-sm font-medium text-foreground-700 hover:bg-background-100">
              首页
            </Link>
            <Link to="/search" className="px-3 py-2.5 rounded-md text-sm font-medium text-foreground-700 hover:bg-background-100">
              搜索
            </Link>
            <Link to="/about" className="px-3 py-2.5 rounded-md text-sm font-medium text-foreground-700 hover:bg-background-100">
              关于我们
            </Link>
            <Link to="/contact" className="px-3 py-2.5 rounded-md text-sm font-medium text-foreground-700 hover:bg-background-100">
              联系我们
            </Link>
          </div>
          <p className="px-3 text-xs text-foreground-400 mb-2">资讯分类</p>
          <div className="grid grid-cols-2 gap-1">
            {categories.map((c) => (
              <Link
                key={c.slug}
                to={`/category/${c.slug}`}
                className="flex items-center gap-2 px-3 py-2.5 rounded-md text-sm text-foreground-700 hover:bg-background-100"
              >
                <i className={`${c.icon} text-primary-500`} />
                {c.name}
              </Link>
            ))}
          </div>
        </div>
      )}
    </header>
  );
}